import { type IUser } from "global-types";
import React, { useState, createContext, useEffect } from "react";
import AuthService from "../services/AuthService";

export interface IUserCredentials {
  email: string;
  password: string;
}

type TAuthContextType = {
  user: IUser;
  setUser: (user: IUser) => void;
  setIsAuthenticated: (status: boolean) => void;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
  isAdmin: boolean;
  setIsAdmin: (status: boolean) => void;
  isLoading: boolean;
};

export const AuthContext = createContext<TAuthContextType>(
  {} as TAuthContextType
);

export default function Auth({ children }: { children: React.JSX.Element }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<IUser>({} as IUser);

  const logout = (): Promise<void> =>
    AuthService.authLogout()
      .then(() => {
        setUser({} as IUser);
        setIsAuthenticated(false);
        setIsLoading(false);
      })
      .catch(() => console.log("Logout unsuccessful"));

  // Check authentication status on mount
  useEffect(() => {
    AuthService.checkIsAuthenticated()
      .then((res) => {
        const userData: IUser = res.data._user;
        setUser(userData);
        setIsAuthenticated(true);

        if (userData.userRole === "ADMIN") {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
        setIsLoading(false);
      })
      .catch(() => {
        setIsAuthenticated(false);
        setIsLoading(false);
      });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        setIsAuthenticated,
        isAuthenticated,
        logout,
        isAdmin,
        setIsAdmin,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
